import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, CheckCircle2, Mail } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";

const SERVICE_ZIPS = new Set([
  "85301", "85302", "85303", "85304", "85305", "85306", "85307", "85308", "85309", "85310", "85318",
  "85345", "85381", "85382", "85383",
  "85374", "85378", "85379", "85387",
  "85323", "85392",
  "85338", "85395",
  "85335", "85351", "85373", "85353", "85340", "85363",
  "85031", "85033", "85035", "85037",
]);

const AREAS = [
  { name: "Glendale, Arizona", note: "Our home base and the heart of the pilot program." },
  { name: "The West Valley", note: "Peoria, Surprise, Avondale, Goodyear, El Mirage, Sun City, and neighboring communities." },
  { name: "Surrounding Areas", note: "Reach out — if we can coordinate transportation, we'll do our best to help." },
];

export default function ServiceArea() {
  const [zip, setZip] = useState("");
  const [result, setResult] = useState(null);

  const check = (e) => {
    e.preventDefault();
    const z = zip.trim();
    if (!/^\d{5}$/.test(z)) {
      setResult({ ok: false, msg: "Please enter a valid 5-digit ZIP code." });
      return;
    }
    setResult(
      SERVICE_ZIPS.has(z)
        ? { ok: true, msg: "Great news — you're in our pilot service area! Start a donation or apply to shop today." }
        : { ok: false, msg: "Pickup availability depends on location, scheduling, transportation, and program capacity. Email us and we'll let you know what's possible." }
    );
  };

  return (
    <div data-testid="service-area-page">
      <PageHero overline="Service Area" title="PROUDLY SERVING" italic="the West Valley." testid="service-area-headline">
        Fresh Start Furnishings is a pilot program growing carefully across the West Valley of Phoenix, Arizona.
      </PageHero>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="grid gap-6 md:grid-cols-3">
          {AREAS.map((a, i) => (
            <FadeUp key={a.name} delay={i * 0.12}>
              <div data-testid={`area-card-${i}`} className="bento-card h-full">
                <MapPin className="h-8 w-8 text-burgundy" />
                <h2 className="mt-4 font-serif text-2xl text-olive">{a.name}</h2>
                <p className="mt-3 text-sm leading-relaxed text-moss">{a.note}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.2}>
          <div className="mt-16 rounded-3xl border border-linen bg-white/60 p-8 sm:p-12" data-testid="zip-checker">
            <h2 className="font-serif text-2xl text-olive sm:text-3xl">Check your ZIP code</h2>
            <p className="mt-2 text-sm text-moss">See if your neighborhood is inside our current pickup and delivery area.</p>
            <form onSubmit={check} className="mt-6 flex flex-col gap-3 sm:flex-row">
              <input
                data-testid="service-area-zip-check-input"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="e.g. 85301"
                maxLength={5}
                inputMode="numeric"
                className="input-field sm:max-w-xs"
              />
              <button type="submit" data-testid="zip-check-button" className="btn-primary">Check Availability</button>
            </form>
            {result && (
              <div
                data-testid="zip-check-result"
                className={`mt-5 flex items-start gap-3 rounded-xl border p-4 text-sm ${
                  result.ok ? "border-olive/40 bg-beige text-olive" : "border-burgundy/30 bg-burgundy/5 text-burgundy"
                }`}
              >
                {result.ok ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> : <Mail className="mt-0.5 h-5 w-5 shrink-0" />}
                <span>{result.msg}</span>
              </div>
            )}
            <p className="mt-6 text-xs leading-relaxed text-sage">
              Note: pickup availability depends on location, scheduling, transportation, and program capacity.
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={0.25} className="mt-12 flex flex-wrap gap-3">
          <Link to="/donate-items" data-testid="area-donate-button" className="btn-accent">Donate Items</Link>
          <Link to="/apply-to-shop" data-testid="area-apply-button" className="btn-primary">Apply to Shop</Link>
        </FadeUp>
      </section>
    </div>
  );
}
